/**
 * @file Mémorise et restaure la position de défilement d'une page de listing entre deux visites.
 * @module packages.common.hooks.useScrollRestoration
 *
 * Une application monopage perd la position de défilement à chaque navigation :
 * le navigateur ne connaît pas la hauteur d'une liste rendue après chargement.
 * Ce hook enregistre la position de la fenêtre par clé (typiquement l'URL du
 * listing, filtres compris) dans sessionStorage, et la rétablit au retour une
 * fois les données affichées.
 *
 * Deux pièges de l'application monopage sont traités ici :
 *   - au démontage d'une page, React exécute les nettoyages d'effets après avoir
 *     retiré son DOM ; la page suivante étant plus courte, le navigateur a déjà
 *     ramené le défilement à zéro et émet un événement de défilement. L'écouteur
 *     est retiré de façon synchrone pendant le commit (effet de layout) et la
 *     position retenue est celle du dernier défilement observé avant ;
 *   - au retour, le contenu peut n'avoir sa hauteur finale qu'après quelques
 *     rendus. La restauration réessaie sur plusieurs frames tant que la page est
 *     trop courte pour atteindre la position, et passe après le défilement vers
 *     le haut appliqué globalement à chaque changement de route (useScrollToTop).
 */

import {useEffect, useLayoutEffect, useRef} from 'react';

const STORAGE_PREFIX = 'scroll-restoration:';
/** Nombre de frames pendant lesquelles on attend que la page soit assez haute pour restaurer. */
const MAX_RESTORE_ATTEMPTS = 30;

/**
 * Lit une position enregistrée.
 *
 * @param {string} key - Clé de page
 * @return {number} Position verticale, 0 si absente ou stockage indisponible
 */
function readPosition(key) {
  try {
    const value = window.sessionStorage.getItem(`${STORAGE_PREFIX}${key}`);
    const parsed = value === null ? 0 : Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  } catch {
    return 0;
  }
}

/**
 * Enregistre une position.
 *
 * @param {string} key - Clé de page
 * @param {number} position - Position verticale
 */
function writePosition(key, position) {
  try {
    window.sessionStorage.setItem(`${STORAGE_PREFIX}${key}`, String(Math.round(position)));
  } catch {
    // Stockage indisponible (navigation privée, quota) : la restauration est simplement inactive.
  }
}

/**
 * Hauteur maximale de défilement actuelle de la fenêtre.
 *
 * @return {number} Position verticale maximale atteignable
 */
function getMaxScrollY() {
  const root = document.documentElement;
  return Math.max(0, root.scrollHeight - window.innerHeight);
}

/**
 * Restaure la position de défilement d'un listing et l'enregistre au fil de la navigation.
 *
 * @param {Object} options - Options
 * @param {string} options.key - Identité de la page (URL avec ses paramètres)
 * @param {boolean} options.ready - Vrai quand le contenu est affiché
 */
export function useScrollRestoration({key, ready}) {
  const restoredKeyRef = useRef(null);
  const lastObservedRef = useRef(null);

  // Enregistrement : la position est relevée à chaque événement de défilement et
  // écrite au plus une fois par frame ; le nettoyage écrit la dernière valeur relevée.
  // Effet de layout : son nettoyage est synchrone dans le commit, donc l'écouteur
  // est retiré avant que le navigateur n'émette le défilement à zéro provoqué par
  // le raccourcissement de la page.
  useLayoutEffect(() => {
    let frame = null;
    lastObservedRef.current = null;

    const persistObserved = () => {
      if (lastObservedRef.current !== null) writePosition(key, lastObservedRef.current);
    };

    const handleScroll = () => {
      lastObservedRef.current = window.scrollY;
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        persistObserved();
      });
    };

    window.addEventListener('scroll', handleScroll, {passive: true});

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (frame !== null) window.cancelAnimationFrame(frame);
      persistObserved();
    };
  }, [key]);

  // Restauration : une seule fois par clé, dès que le contenu est affiché et que
  // la page est assez haute pour atteindre la position mémorisée.
  useEffect(() => {
    if (!ready || restoredKeyRef.current === key) return undefined;
    restoredKeyRef.current = key;

    const position = readPosition(key);
    if (position === 0) return undefined;

    let attempts = 0;
    let frame = null;

    const tryRestore = () => {
      attempts += 1;
      if (getMaxScrollY() >= position || attempts >= MAX_RESTORE_ATTEMPTS) {
        window.scrollTo(0, position);
        frame = null;
        return;
      }
      frame = window.requestAnimationFrame(tryRestore);
    };

    frame = window.requestAnimationFrame(tryRestore);
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [key, ready]);
}
